import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import NotificationsBell from "@/components/NotificationsBell";
import { currentTheme } from "@/lib/theme";

const STAFF_ROLES = ["admin", "superadmin", "parent", "kid_worker", "content_editor"];
const ADMIN_TAB_ROLES = ["admin", "superadmin", "parent"];
const CMS_ROLES = ["content_editor", "superadmin", "parent"];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  const role = user?.role;
  const isStaff = role && STAFF_ROLES.includes(role);
  const isKidWorker = role === "kid_worker";
  const isAdminAbove = role && ADMIN_TAB_ROLES.includes(role);
  const isCmsRole = role && CMS_ROLES.includes(role);

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b-2 border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <img src="/logo.png" alt="Bag Dash Kids" className="h-11 w-11 rounded-2xl object-contain bg-green-600 border-2 border-green-700 p-0.5"/>
          <span className="font-fredoka font-bold text-lg sm:text-xl text-slate-900 leading-none hidden sm:inline">
            Bag Dash <span className="text-green-600">Kids</span>
          </span>
          <span className="text-xl" data-testid="seasonal-emoji">{currentTheme().emoji}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {!isKidWorker && (
            <>
              <Link to="/schedule" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-schedule">Schedule</Link>
              <Link to="/map" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-map">Areas</Link>
              <Link to="/reviews" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-reviews">Reviews</Link>
              <Link to="/rules" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-rules">Rules</Link>
            </>
          )}
          {user && role === "customer" && (
            <>
              <Link to="/dashboard" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/calendar" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-calendar">Calendar</Link>
              <Link to="/plan" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-plan">Plan</Link>
              <Link to="/leaderboard" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-leaderboard">Leaderboard</Link>
              <Link to="/settings" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-settings">Settings</Link>
            </>
          )}
          {isAdminAbove && (
            <>
              <Link to="/admin" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-admin">Admin</Link>
              <Link to="/worker" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-worker">Kid Route</Link>
            </>
          )}
          {isCmsRole && (
            <Link to="/editor" className="px-3 py-2 text-sm font-semibold text-amber-700 hover:text-amber-900" data-testid="nav-editor">Website Editor</Link>
          )}
          {isKidWorker && (
            <Link to="/worker" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-green-700" data-testid="nav-worker">My Route</Link>
          )}
          {user && <div className="ml-2"><NotificationsBell /></div>}
          {user ? (
            <>
              {isStaff && <RoleBadge role={role} />}
              <button onClick={onLogout} className="ml-1 btn-push-outline px-4 py-2 text-sm" data-testid="logout-btn">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-push-outline px-4 py-2 text-sm ml-1" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="btn-push-green px-4 py-2 text-sm ml-1" data-testid="nav-register">Sign up</Link>
            </>
          )}
        </nav>

        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationsBell />}
          <button className="p-2 rounded-xl border-2 border-slate-200" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-slate-100 bg-white px-4 py-3 flex flex-col gap-1">
          {!isKidWorker && (
            <>
              <MLink to="/schedule" onClose={() => setOpen(false)} testid="mnav-schedule">Schedule</MLink>
              <MLink to="/map" onClose={() => setOpen(false)} testid="mnav-map">Service Areas</MLink>
              <MLink to="/reviews" onClose={() => setOpen(false)} testid="mnav-reviews">Reviews</MLink>
              <MLink to="/rules" onClose={() => setOpen(false)} testid="mnav-rules">Service Rules</MLink>
              <MLink to="/about" onClose={() => setOpen(false)} testid="mnav-about">About</MLink>
              <MLink to="/contact" onClose={() => setOpen(false)} testid="mnav-contact">Contact</MLink>
            </>
          )}
          {user && role === "customer" && (
            <>
              <MLink to="/dashboard" onClose={() => setOpen(false)} testid="mnav-dashboard">Dashboard</MLink>
              <MLink to="/calendar" onClose={() => setOpen(false)} testid="mnav-calendar">Calendar</MLink>
              <MLink to="/plan" onClose={() => setOpen(false)} testid="mnav-plan">My Plan</MLink>
              <MLink to="/billing" onClose={() => setOpen(false)} testid="mnav-billing">Billing</MLink>
              <MLink to="/leaderboard" onClose={() => setOpen(false)} testid="mnav-leaderboard">Leaderboard</MLink>
            </>
          )}
          {isAdminAbove && (
            <>
              <MLink to="/admin" onClose={() => setOpen(false)} testid="mnav-admin">Admin</MLink>
              <MLink to="/worker" onClose={() => setOpen(false)} testid="mnav-worker">Kid Route</MLink>
            </>
          )}
          {isCmsRole && (
            <MLink to="/editor" onClose={() => setOpen(false)} testid="mnav-editor">Website Editor</MLink>
          )}
          {isKidWorker && <MLink to="/worker" onClose={() => setOpen(false)} testid="mnav-worker">My Route</MLink>}
          {user ? (
            <>
              {isStaff && <div className="px-3 py-1"><RoleBadge role={role} /></div>}
              <button onClick={onLogout} className="btn-push-outline px-4 py-2 text-sm mt-2" data-testid="mlogout-btn">Log out</button>
            </>
          ) : (
            <div className="flex gap-2 mt-2">
              <Link to="/login" onClick={() => setOpen(false)} className="btn-push-outline px-4 py-2 text-sm flex-1 text-center" data-testid="mnav-login">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="btn-push-green px-4 py-2 text-sm flex-1 text-center" data-testid="mnav-register">Sign up</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

const MLink = ({ to, onClose, testid, children }) => (
  <Link to={to} onClick={onClose} className="px-3 py-2 font-semibold text-slate-700" data-testid={testid}>{children}</Link>
);

const RoleBadge = ({ role }) => {
  const map = {
    parent: { label: "Parent", cls: "bg-purple-100 text-purple-800 border-purple-200" },
    superadmin: { label: "Superadmin", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    admin: { label: "Admin", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    kid_worker: { label: "Kid Worker", cls: "bg-green-100 text-green-800 border-green-200" },
    content_editor: { label: "Editor", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const m = map[role];
  if (!m) return null;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border-2 ${m.cls}`} data-testid="role-badge">
      {m.label}
    </span>
  );
};
